// useStreamSocket(uuid) — exposes { messages, viewers, status, conn, sendChat, fetchOlder }.
// Chat renders on `ack` (optimistic-light); chat buffer capped at ~300 (init-handoff §4.6).
import { useCallback, useEffect, useRef, useState } from "react";
import type { CommentDTO } from "../api/types.js";
import { type ConnState, CborSocket } from "./cborSocket.js";
import type { ChatMsg, StatusMsg } from "./protocol.js";

const MAX_BUFFER = 300;

export interface ChatLine {
  id: string;
  userId: string | null;
  name: string;
  avatar: string | null;
  authorType: string;
  isStreamer: boolean;
  body: string;
  ts: number;
}

function toLine(c: ChatMsg | CommentDTO): ChatLine {
  if ("t" in c) {
    return { id: c.id, userId: c.userId, name: c.name, avatar: c.avatar, authorType: c.authorType, isStreamer: c.isStreamer, body: c.body, ts: c.ts };
  }
  return {
    id: c.id,
    userId: null, // backfilled history carries no author id
    name: c.author.name,
    avatar: c.author.avatar,
    authorType: c.authorType,
    isStreamer: c.author.isStreamer,
    body: c.content,
    ts: c.ts,
  };
}

export interface UseStreamSocket {
  messages: ChatLine[];
  viewers: number;
  status: StatusMsg | null;
  conn: ConnState;
  sysNotice: string | null;
  sendChat: (body: string) => void;
  fetchOlder: () => void;
}

export function useStreamSocket(uuid: string | undefined): UseStreamSocket {
  const [messages, setMessages] = useState<ChatLine[]>([]);
  const [viewers, setViewers] = useState(0);
  const [status, setStatus] = useState<StatusMsg | null>(null);
  const [conn, setConn] = useState<ConnState>("connecting");
  const [sysNotice, setSysNotice] = useState<string | null>(null);
  const sockRef = useRef<CborSocket | null>(null);
  const seenIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!uuid) return;
    seenIds.current = new Set();
    setMessages([]);
    const sock = new CborSocket(uuid, {
      onState: setConn,
      onMessage: (msg) => {
        switch (msg.t) {
          case "chat": {
            if (seenIds.current.has(msg.id)) return;
            seenIds.current.add(msg.id);
            setMessages((prev) => [...prev, toLine(msg)].slice(-MAX_BUFFER));
            break;
          }
          case "viewers":
            setViewers(msg.count);
            break;
          case "status":
            setStatus(msg);
            break;
          case "comments": {
            const older = msg.comments.filter((c) => !seenIds.current.has(c.id));
            older.forEach((c) => seenIds.current.add(c.id));
            setMessages((prev) => [...older.map(toLine), ...prev].slice(-MAX_BUFFER));
            break;
          }
          case "sys":
            setSysNotice(msg.body);
            break;
          case "ack":
            break;
        }
      },
    });
    sockRef.current = sock;
    sock.connect();
    // Fetch chat history once connected.
    const t = setTimeout(() => sock.send({ t: "fetchComments", limit: 50 }), 400);
    return () => {
      clearTimeout(t);
      sock.close();
      sockRef.current = null;
    };
  }, [uuid]);

  const sendChat = useCallback((body: string) => {
    const trimmed = body.trim();
    if (!trimmed) return;
    sockRef.current?.send({ t: "chat", body: trimmed, nonce: `${Date.now()}-${Math.random()}` });
  }, []);

  const fetchOlder = useCallback(() => {
    const oldest = messages[0]?.ts;
    sockRef.current?.send({ t: "fetchComments", limit: 50, beforeTs: oldest });
  }, [messages]);

  return { messages, viewers, status, conn, sysNotice, sendChat, fetchOlder };
}
